/* eslint-disable @typescript-eslint/no-unused-expressions */
import React from "react";

import { ApolloCache, FetchResult, useMutation } from "@apollo/client";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Typography,
} from "@mui/material";
import { FileUploader } from "react-drag-drop-files";
import { Controller, useForm } from "react-hook-form";
import Iframe from "react-iframe";
import { Link, useParams } from "react-router-dom";

import CustomModal from "../../../Components/CustomModal/CustomModal";
import CustomSnackBar from "../../../Components/CustomSnackBar/CustomSnackBar";
import CustomTextInput from "../../../Components/CustomTextInput/CustomTextInput";
import Loading from "../../../Components/Loading/Loading";
import {
  MAX_ORDONNANCE_FILES,
  MAX_ORDONNANCE_FILE_SIZE,
  MAX_ORDONNANCE_TOTAL_SIZE,
  ORDONNANCE_IMAGE_MAX_EDGE,
  ORDONNANCE_IMAGE_QUALITY,
  RulesPlanification,
  checkTypeFile,
  formatMo,
  initRulesPlanification,
  validationSchema,
} from "../../../Config/constant";
import AuthContext from "../../../Context/AuthContext";
import theme from "../../../Theme/theme";
import { downscaleImage } from "../../../Utils/services";
import {
  Mutation,
  MutationCreateOrderArgs,
  Query,
  QueryGetListCommandesArgs,
} from "../../../graphql/__generated__/types";
import { CREATE_ORDER } from "../../../graphql/command/mutation";
import { GET_ORDER_BY_ID } from "../../../graphql/command/query";
import CustomPlanner from "../../Planification/CustomPlanification/CustomPlanner";
import CustomSelect from "../common/CustomSelect";
import ModalConfirmation from "../common/ModalConfirmation";
import { useNewStyle } from "./NewStyle";

export interface FormOrder {
  comment: string;
  doctorName: string;
  images?: string;
  orderFormule: string;
  patientName: string;
  pharmaceuticalForm: string;
  status?: string;
  NourissonsOrAnimals?: string;
  typePatient: string | undefined;
  validationContrat: boolean;
}

const defaultValues: FormOrder = {
  comment: "",
  doctorName: "",
  orderFormule: "",
  patientName: "",
  pharmaceuticalForm: "",
  typePatient: "",
  validationContrat: false,
};
const BLOCKED_MESSAGE =
  "Vous ne pouvez pas valider cette action, car votre compte est bloqué.\nNous vous invitons à vous rapprocher de notre Pharmacie pour de plus amples informations.";

interface OrdonnanceFile {
  /** identifiant stable : l'index ne l'est pas, on peut retirer un fichier du milieu */
  id: string;
  content: string;
  name: string;
  size: number;
}

const NewAddCommand: React.FC = () => {
  const { Me } = React.useContext(AuthContext);
  const isBlocked = !!Me?.client?.bloquer;
  const [files, setFiles] = React.useState<OrdonnanceFile[]>([]);
  const nextFileId = React.useRef(0);
  const [open, setOpen] = React.useState<boolean>(false);

  const [planification, setPlanification] = React.useState<RulesPlanification>({
    rules: initRulesPlanification.rules,
  });

  const [openToast, setopenToast] = React.useState(false);
  const [message, setMessage] = React.useState("");

  const [openModal, setopenModal] = React.useState(false);

  const style = useNewStyle();
  const { type: typeCommande } = useParams();

  const {
    control,
    handleSubmit,
    formState: { errors },
    // getFieldState,
    watch,
    reset,
  } = useForm<FormOrder>({ defaultValues });

  const openCloseModalConfirmation = () => setOpen((prev) => !prev);

  const resetPlanification = () =>
    setPlanification({ rules: initRulesPlanification.rules });

  const [DoCreateOrder, { loading }] = useMutation<
    Mutation,
    MutationCreateOrderArgs
  >(CREATE_ORDER, {
    update: (
      cache: ApolloCache<any>,
      result: Omit<FetchResult<Mutation>, "context">,
    ) => {
      if (result.data?.CreateOrder?.id) {
        openCloseModalConfirmation();
        reset(defaultValues);
        resetPlanification();
        setFiles([]);
        const resultat = {
          ...result.data?.CreateOrder,
          __typename: "Commande",
        };

        const allQuerySended = cache.readQuery<
          Query,
          QueryGetListCommandesArgs
        >({
          query: GET_ORDER_BY_ID,
          variables: {
            input: {
              take: 50,
              skip: 0,
              TypeCommandes: 3,
            },
          },
        });

        const dataUpdated = [
          resultat,
          ...(allQuerySended?.GetListCommandes || []),
        ];

        cache.writeQuery<Query, QueryGetListCommandesArgs>({
          query: GET_ORDER_BY_ID,
          variables: {
            input: {
              take: allQuerySended?.GetListCommandes?.length || 0,
              skip: 0,
              TypeCommandes: 3,
            },
          },
          data: {
            GetListCommandes: dataUpdated as any,
          },
        });

        if (resultat?.renouvellement) {
          const allQueryRenouvellement = cache.readQuery<
            Query,
            QueryGetListCommandesArgs
          >({
            query: GET_ORDER_BY_ID,
            variables: {
              input: {
                take: 50,
                skip: 0,
                TypeCommandes: 1,
              },
            },
          });
          const dataUpdatedRenouvellemnt = [
            resultat,
            ...(allQueryRenouvellement?.GetListCommandes || []),
          ];

          cache.writeQuery<Query, QueryGetListCommandesArgs>({
            query: GET_ORDER_BY_ID,
            variables: {
              input: {
                take: allQueryRenouvellement?.GetListCommandes?.length || 0,
                skip: 0,
                TypeCommandes: 1,
              },
            },
            data: {
              GetListCommandes: dataUpdatedRenouvellemnt as any,
            },
          });
        }
      }
    },
  });
  /** Verrou synchrone contre le double clic, voir `handleCreate`. */
  const submitDispatched = React.useRef(false);

  // Le déverrouillage suit l'état de chargement plutôt qu'un rappel de la
  // mutation : avec `errorPolicy: "all"` un échec ne déclenche pas forcément
  // `onError`, et le verrou resterait fermé — l'utilisateur ne pourrait plus
  // renvoyer sa commande sans recharger la page.
  React.useEffect(() => {
    if (!loading) {
      submitDispatched.current = false;
    }
  }, [loading]);

  const ShowMessageError = (text: string) => {
    setopenToast(true);
    setMessage(text);
  };

  const onValidate = () => {
    validationSchema
      .validate({
        repeatNumber: Number(planification.rules.repeatNumber),
        frequency: planification.rules.repeatType,
      })
      // eslint-disable-next-line consistent-return
      .then(() => {
        const { endType, endValue, repeatType, selectedDays } =
          planification.rules;
        if (repeatType === "week" && selectedDays.length === 0) {
          return ShowMessageError(
            "Veuillez sélectionner au moins un jour de repetition",
          );
        }
        if (endType) {
          if (endType === "at" && !endValue) {
            return ShowMessageError("Veuillez ecrire la date de fin");
          }
          if (endType === "after" && !endValue) {
            return ShowMessageError("Veuillez ecrire le nombre d'occurence");
          }

          return setopenModal((prev) => !prev);
        }
        return ShowMessageError("Veuillez sélectionner se termine");
      })
      .catch((error) => ShowMessageError(error.message));
  };

  const isRenouvellement =
    planification.rules.repeatNumber &&
    planification.rules.repeatType &&
    planification.rules.endValue;

  const handleCreate = async (data: FormOrder) => {
    // `handleSubmit` valide le formulaire de façon asynchrone : pendant ce
    // laps de temps `loading` est encore faux, le voile de chargement n'est pas
    // affiché et le bouton reste cliquable. Ce verrou synchrone ferme cette
    // fenêtre, où passaient les doubles clics — et donc les commandes en double.
    if (submitDispatched.current || loading) {
      return;
    }
    submitDispatched.current = true;

    try {
      const result = await DoCreateOrder({
        variables: {
          input: {
            ...data,
            typePatient: data.typePatient || "",
            files: files.map((ordonnance) => ({ content: ordonnance.content })),
            typeCommande: Number(typeCommande || 3),
            renouvellement:
              typeCommande === "3" && isRenouvellement
                ? JSON.stringify(planification)
                : null,
          },
        },
      });

      // Le client est configuré en `errorPolicy: "all"` : une erreur GraphQL ne
      // rejette pas la promesse et ne déclenche pas `onError`, elle revient
      // dans `errors` à côté de données nulles. Sans cette lecture, un refus du
      // serveur — le mail au préparatoire qui n'est pas parti, par exemple — ne
      // produisait aucun message : le voile de chargement disparaissait et rien
      // d'autre.
      const [firstError] = result.errors || [];

      if (firstError) {
        ShowMessageError(firstError.message);
      }
    } catch (error: any) {
      ShowMessageError(
        error?.message || "L'envoi a échoué, merci de réessayer.",
      );
    }
  };

  const readFile = (f: File) =>
    new Promise<OrdonnanceFile>((resolve, reject) => {
      const reader: FileReader = new FileReader();

      reader.onloadend = () => {
        nextFileId.current += 1;
        resolve({
          id: `ordonnance-${nextFileId.current}`,
          content: String(reader.result),
          name: f.name,
          size: f.size,
        });
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(f);
    });

  // Avec `multiple`, react-drag-drop-files renvoie une FileList ; sans, un File.
  const handleChange = async (selection: FileList | File) => {
    const dropped =
      selection instanceof File ? [selection] : Array.from(selection);
    const room = MAX_ORDONNANCE_FILES - files.length;

    if (room <= 0) {
      ShowMessageError(
        `Vous ne pouvez joindre que ${MAX_ORDONNANCE_FILES} fichiers.`,
      );
      return;
    }
    if (dropped.length > room) {
      ShowMessageError(
        `Vous ne pouvez joindre que ${MAX_ORDONNANCE_FILES} fichiers : seul(s) le(s) ${room} premier(s) est/sont retenu(s).`,
      );
    }

    // Le cadrage précède le contrôle de taille : c'est le poids après
    // redimensionnement qui doit être comparé aux plafonds, sinon une photo
    // parfaitement acceptable une fois cadrée serait refusée sur sa taille
    // d'origine. Seuls les fichiers retenus sont cadrés — décoder les autres
    // serait du travail perdu sur une image de plusieurs mégapixels.
    const selected = await Promise.all(
      dropped
        .slice(0, room)
        .map((file) =>
          downscaleImage(
            file,
            ORDONNANCE_IMAGE_MAX_EDGE,
            ORDONNANCE_IMAGE_QUALITY,
          ),
        ),
    );

    const usedSize = files.reduce(
      (total, ordonnance) => total + ordonnance.size,
      0,
    );

    // Taille contrôlée avant lecture : inutile de charger en mémoire un fichier
    // qui sera refusé. Les mêmes plafonds sont revalidés par le backend.
    let running = usedSize;
    let totalExceeded = false;
    const accepted: File[] = [];

    selected.slice(0, room).forEach((candidate) => {
      if (candidate.size > MAX_ORDONNANCE_FILE_SIZE) {
        ShowMessageError(
          `${candidate.name} dépasse ${formatMo(MAX_ORDONNANCE_FILE_SIZE)}.`,
        );
        return;
      }
      if (running + candidate.size > MAX_ORDONNANCE_TOTAL_SIZE) {
        totalExceeded = true;
        return;
      }
      running += candidate.size;
      accepted.push(candidate);
    });

    if (totalExceeded) {
      ShowMessageError(
        `Les ordonnances ne doivent pas dépasser ${formatMo(
          MAX_ORDONNANCE_TOTAL_SIZE,
        )} au total.`,
      );
    }
    if (accepted.length === 0) {
      return;
    }

    const read = await Promise.all(accepted.map(readFile));
    setFiles((prev) => [...prev, ...read]);
  };

  const removeFile = (index: number) =>
    setFiles((prev) => prev.filter((_, i) => i !== index));

  const isFileLimitReached = files.length >= MAX_ORDONNANCE_FILES;
  const hasFile = files.length > 0;

  // L'utilisateur ne devinait pas qu'un second document était possible : on le
  // dit avant le dépôt, et on le redit une fois le premier déposé.
  const instruction = (() => {
    if (isFileLimitReached) {
      return `Maximum de ${MAX_ORDONNANCE_FILES} documents atteint`;
    }
    if (hasFile) {
      return "Vous pouvez ajouter un 2e document";
    }
    return `Vous pouvez joindre jusqu'à ${MAX_ORDONNANCE_FILES} documents`;
  })();

  // Un emplacement libre s'affiche à côté du premier document : il occupe une
  // colonne, donc il compte dans le choix du style de vignette.
  const showEmptySlot = hasFile && !isFileLimitReached;
  const previewColumns = files.length + (showEmptySlot ? 1 : 0);
  const previewClassName = previewColumns > 1 ? style.imgMultiple : style.img;

  const fileTypes = ["JPG", "JPEG", "jpeg", "jpg", "PNG", "PDF", "pdf"];

  const TypePatient = [
    { label: "Nourrisson - < 30 mois", value: "4" },
    { label: "Pédiatrie - 0 à 15 ans", value: "1" },
    { label: "Adulte", value: "2" },
    { label: "Vétérinaire", value: "0" },
  ];

  React.useEffect(() => {
    reset(defaultValues);
    setFiles([]);
  }, [reset, typeCommande]);

  return (
    <div className={style.container}>
      <div className={style.rootTopBox}>
        <Loading loading={loading} />
        <CustomSnackBar
          message={message}
          open={openToast}
          setOpen={setopenToast}
          severity="error"
        />
        <CustomModal
          open={open}
          setOpen={setOpen}
          content={<ModalConfirmation type={typeCommande} />}
          confirmAction={{
            text: "Ok",
            style: {
              background: "#ffffff",
              boxShadow:
                "0 3px 1px -2px rgba(0,0,0,.2), 0 2px 2px 0 rgba(0,0,0,.14), 0 1px 5px 0 rgba(0,0,0,.12)",
              color: "#000",
              fontWeight: "bold",
            },
            onClick: () => {
              openCloseModalConfirmation();
            },
          }}
        />
        <CustomModal
          open={openModal}
          setOpen={setopenModal}
          title="Commande différée ou planifier un renouvellement"
          content={
            <div>
              <CustomPlanner
                planification={planification}
                setPlanification={setPlanification}
              />
              <Typography>
                Veuillez renseigner les dates auxquelles nous recevons vos
                demandes.
              </Typography>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <Button
                  size="large"
                  color="error"
                  variant="contained"
                  style={{ textTransform: "unset" }}
                  onClick={() => {
                    resetPlanification();
                    setopenModal((prev) => !prev);
                  }}
                >
                  Annuler
                </Button>
                <Button
                  size="large"
                  color="primary"
                  variant="contained"
                  style={{ marginLeft: 10, textTransform: "unset" }}
                  onClick={onValidate}
                >
                  Confirmer
                </Button>
              </div>
            </div>
          }
        />
        <div className={style.boxSelect}>
          <div className={style.rootSelect}>
            <Controller
              name="typePatient"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: "Ce champ est requis",
                },
              }}
              render={({ field: { value, onChange } }) => (
                <CustomSelect
                  label="Type de patient"
                  data={TypePatient}
                  setValue={onChange}
                  value={value}
                  errors
                  errorLabel={errors.typePatient?.message}
                />
              )}
            />
            {watch("typePatient") && watch("typePatient") !== "2" && (
              <Controller
                name="NourissonsOrAnimals"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CustomTextInput
                    id="adornment-weight"
                    InputLabelProps={{ style: { fontSize: "12px" } }}
                    label={
                      watch("typePatient") === "4" ||
                      watch("typePatient") === "1"
                        ? "Poids"
                        : "Informations sur l'animal"
                    }
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
            )}
          </div>
          <Controller
            name="pharmaceuticalForm"
            control={control}
            rules={{
              required: {
                value: !hasFile,
                message: "Ce champ est requis",
              },
            }}
            render={({ field: { onChange, value } }) => (
              <CustomTextInput
                error={!hasFile}
                color="primary"
                id="galénique"
                label="Forme galénique"
                multiline
                rows={3}
                value={value}
                onChange={onChange}
                FormHelperTextProps={{
                  style: { background: "white", margin: "0" },
                }}
                helperText={!hasFile ? errors.pharmaceuticalForm?.message : ""}
                style={{
                  width: "269px",
                  background: "#F9F9F9",
                  borderRadius: "6px",
                }}
              />
            )}
          />
        </div>
        <Controller
          name="orderFormule"
          control={control}
          rules={{
            required: {
              value: !hasFile,
              message: "Ce champ est requis",
            },
          }}
          render={({ field: { onChange, value } }) => (
            <CustomTextInput
              error={!hasFile}
              id="commande"
              value={value}
              label="Formule de la commande"
              rows={3}
              multiline
              onChange={onChange}
              helperText={!hasFile ? errors.orderFormule?.message : ""}
              FormHelperTextProps={{
                style: { background: "white", margin: "0" },
              }}
              style={{
                width: "411px",
                background: "#F9F9F9",
                borderRadius: "6px",
              }}
            />
          )}
        />
      </div>
      <div className={style.boxCard}>
        <div className={style.rootCard}>
          <Typography style={{ fontWeight: "bold", margin: "0 0 20px 0" }}>
            Déposer vos ordonnances
          </Typography>
          <Typography className={style.instruction}>{instruction}</Typography>

          <FileUploader
            handleChange={handleChange}
            name="file"
            types={fileTypes}
            multiple
            disabled={isFileLimitReached}
            // onTypeError={(err: any) => console.log(err)}
          >
            <div
              className={style.inputDrag}
              style={{
                background:
                  typeCommande === "2"
                    ? theme.palette.primary.main
                    : theme.palette.primary.dark,
              }}
            >
              {/* En `position: absolute`, ce texte transparaîtrait au travers
                  de l'emplacement libre : il ne sert plus une fois un document
                  déposé, la ligne au-dessus de la zone prenant le relais. */}
              {!hasFile && (
                <Typography
                  style={{
                    fontSize: "25px",
                    width: "291px",
                    position: "absolute",
                    color: "#FFFFFF",
                  }}
                >
                  Glisser déposer vos documents ici
                </Typography>
              )}
              {hasFile && (
                <div className={style.previewList}>
                  {files.map((ordonnance, index) =>
                    checkTypeFile(ordonnance.content) ? (
                      <Iframe
                        key={ordonnance.id}
                        loading="auto"
                        url={ordonnance.content}
                        id=""
                        className={previewClassName}
                        display="block"
                        position="relative"
                      />
                    ) : (
                      <img
                        key={ordonnance.id}
                        src={ordonnance.content}
                        alt={`ordonnance ${index + 1}`}
                        className={previewClassName}
                      />
                    ),
                  )}
                  {showEmptySlot && (
                    <div className={style.emptySlot}>
                      <AddIcon fontSize="large" />
                      <Typography style={{ fontSize: "13px" }}>
                        2e document possible
                      </Typography>
                    </div>
                  )}
                </div>
              )}
            </div>
          </FileUploader>
          {files.map((ordonnance, index) => (
            <div className={style.boxSubtTileImg} key={ordonnance.id}>
              <Typography>
                {files.length > 1
                  ? `Document ${index + 1} — ${ordonnance.name}`
                  : ordonnance.name}
              </Typography>
              <Button
                size="large"
                style={{
                  width: "207px",
                  color: "red",
                  textTransform: "unset",
                }}
                onClick={() => removeFile(index)}
              >
                Annuler la séléction
              </Button>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Typography style={{ fontWeight: "bold", margin: "0 0 12px 0" }}>
            Informations complémentaires
          </Typography>
          <Controller
            name="doctorName"
            control={control}
            render={({ field: { onChange, value } }) => (
              <CustomTextInput
                id="prescription"
                label="Nom du prescripteur"
                onChange={onChange}
                value={value}
                style={{
                  width: "100%",
                  border: "1px solid #FFE6E6",
                  borderRadius: "6px",
                  background: "#F9F9F9",
                }}
              />
            )}
          />
          <Controller
            name="patientName"
            control={control}
            render={({ field: { onChange, value } }) => (
              <CustomTextInput
                id="patient"
                label="Nom du patient"
                onChange={onChange}
                value={value}
                style={{
                  width: "411px",
                  border: "1px solid #FFE6E6",
                  background: "#F9F9F9",
                  borderRadius: "6px",
                }}
              />
            )}
          />
          <Controller
            name="comment"
            control={control}
            render={({ field: { onChange, value } }) => (
              <CustomTextInput
                id="commentaire"
                label="Commentaire"
                rows={3}
                onChange={onChange}
                multiline
                value={value}
                style={{
                  width: "411px",
                  border: "1px solid #FFE6E6",
                  background: "#F9F9F9",
                  borderRadius: "6px",
                }}
              />
            )}
          />
        </div>
      </div>
      <div>
        <div style={{ padding: "0px 0px 0px 30px" }}>
          <Controller
            name="validationContrat"
            control={control}
            rules={{
              required: {
                value: true,
                message: "Ce champ est requis",
              },
            }}
            render={({ field: { value, ...field } }) => (
              <FormControlLabel
                style={{
                  display: "flex",
                  alignSelf: "center",
                  justifyContent: "center",
                  alignItems: "start",
                }}
                control={
                  <Checkbox
                    {...field}
                    checked={!!value}
                    style={{
                      color: errors.validationContrat?.message
                        ? "red"
                        : undefined,
                    }}
                  />
                }
                label={
                  <Typography
                    style={{
                      maxWidth: "100%",
                      textAlign: "start",
                      color: "#00000095",
                    }}
                  >
                    {`En cochant cette case, vous reconnaissez avoir décidé de la
                    sous-traitance de la préparation et avoir transmis les
                    informations que vous détenez dans l’intérêt du patient.
                    Vous déclarez également passer commande/devis en
                    connaissance de la réglementation et du cadre contractuel
                    qui vous lie à votre sous-traitant ( pour plus d’information `}
                    <Link
                      target="_blank"
                      to="https://ansm.sante.fr/documents/reference/bonnes-pratiques-de-preparation"
                    >
                      cliquez ici
                    </Link>
                    {` )`}
                  </Typography>
                }
              />
            )}
          />
        </div>
        <div style={{ height: "31px", margin: "5px 0 15px 0" }}>
          {typeCommande === "3" &&
            (isRenouvellement ? (
              <div className={style.boxSavegardPlanner}>
                <Typography
                  style={{ color: theme.palette.primary.main, fontSize: 16 }}
                >
                  Planification sauvegarde{" "}
                </Typography>
                <IconButton
                  aria-label="close"
                  color="secondary"
                  onClick={resetPlanification}
                >
                  <CloseIcon />
                </IconButton>
              </div>
            ) : (
              <Button
                style={{
                  textTransform: "unset",
                  color: "#000",
                  background: "#F9F9F9",
                }}
                onClick={() => setopenModal((prev) => !prev)}
                variant="contained"
                color="inherit"
              >
                Commande différée ou planifier un renouvellement
              </Button>
            ))}
        </div>
        {isBlocked && (
          <Alert
            severity="error"
            style={{ marginBottom: 8, whiteSpace: "pre-line" }}
          >
            {BLOCKED_MESSAGE}
          </Alert>
        )}
        <Button
          style={{
            width: 193,
            height: 41,
            backgroundImage: `ùm$`,
          }}
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSubmit(handleCreate)}
          disabled={isBlocked || loading}
        >
          {loading ? "Envoi en cours…" : "Envoyer"}
        </Button>
      </div>
    </div>
  );
};

export default NewAddCommand;
